import { useEffect, useMemo, useState } from "react";

import Api from "../../../shared/api/api";
import AddTestForm from "./AddTestForm";

export default function TestsAdminList({
  tests = [],
  setTests,
  testsActiveModal = false,
  setTestsActiveModal,
}) {
  const [formData, setFormData] = useState({});
  const [isAddTestIdOpen, setIsAddTestIdOpen] =
    useState(false);

  const [newTestTitle, setNewTestTitle] =
    useState("");

  const [updatingId, setUpdatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [isAddingTest, setIsAddingTest] =
    useState(false);

  const [error, setError] = useState("");
  const [status, setStatus] = useState("");

  useEffect(() => {
    const initialFormData = tests.reduce(
      (currentData, test) => {
        currentData[test.id] = {
          title: test.title || "",
        };

        return currentData;
      },
      {}
    );

    setFormData(initialFormData);
  }, [tests]);

  const nextTestId = useMemo(() => {
    if (tests.length === 0) {
      return 1;
    }

    const largestId = Math.max(
      ...tests.map(
        (test) => Number(test.id) || 0
      )
    );

    return largestId + 1;
  }, [tests]);

  const handleInputChange = (testId, value) => {
    setFormData((currentData) => ({
      ...currentData,
      [testId]: {
        ...currentData[testId],
        title: value,
      },
    }));

    setError("");
    setStatus("");
  };

  const openAddTestPanel = () => {
    setNewTestTitle("");
    setError("");
    setStatus("");
    setIsAddTestIdOpen(true);
  };

  const closeAddTestPanel = () => {
    setNewTestTitle("");
    setIsAddTestIdOpen(false);
  };

  const handleAddTest = async (event) => {
    event.preventDefault();

    const title = newTestTitle.trim();

    if (!title) {
      setError("Please enter a test title.");
      return;
    }

    setIsAddingTest(true);
    setError("");
    setStatus("");

    try {
      const response = await Api.addTest({
        title,
      });

      if (response?.error) {
        throw new Error(response.error);
      }

      const addedTest =
        response?.test || response;

      if (
        typeof setTests === "function" &&
        addedTest
      ) {
        setTests((currentTests) => [
          ...currentTests,
          {
            ...addedTest,
            id: addedTest.id ?? nextTestId,
            title:
              addedTest.title ?? title,
          },
        ]);
      }

      setStatus("The test was added successfully.");
      closeAddTestPanel();
    } catch (requestError) {
      console.error(
        "Unable to add FourH test:",
        requestError
      );

      setError(
        requestError?.message ||
          "The test could not be added."
      );
    } finally {
      setIsAddingTest(false);
    }
  };

  const handleUpdate = async (testId) => {
    const title =
      formData[testId]?.title?.trim() || "";

    if (!title) {
      setError("A test title is required.");
      return;
    }

    setUpdatingId(testId);
    setError("");
    setStatus("");

    try {
      const response = await Api.updateTest(
        testId,
        title
      );

      if (response?.error) {
        throw new Error(response.error);
      }

      if (typeof setTests === "function") {
        setTests((currentTests) =>
          currentTests.map((test) =>
            test.id === testId
              ? {
                  ...test,
                  title,
                }
              : test
          )
        );
      }

      setStatus(`Test ${testId} was updated.`);
    } catch (requestError) {
      console.error(
        "Unable to update FourH test:",
        requestError
      );

      setError(
        requestError?.message ||
          "The test could not be updated."
      );
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDelete = async (testId) => {
    const confirmed = window.confirm(
      `Delete test ${testId}? This may also affect its questions and assessments.`
    );

    if (!confirmed) {
      return;
    }

    setDeletingId(testId);
    setError("");
    setStatus("");

    try {
      const response = await Api.deleteSingle(
        "test",
        testId,
        ""
      );

      if (response?.error) {
        throw new Error(response.error);
      }

      if (typeof setTests === "function") {
        setTests((currentTests) =>
          currentTests.filter(
            (test) => test.id !== testId
          )
        );
      }

      setStatus(`Test ${testId} was deleted.`);
    } catch (requestError) {
      console.error(
        "Unable to delete FourH test:",
        requestError
      );

      setError(
        requestError?.message ||
          "The test could not be deleted."
      );
    } finally {
      setDeletingId(null);
    }
  };

  const toggleQuestionForm = () => {
    if (
      typeof setTestsActiveModal === "function"
    ) {
      setTestsActiveModal(
        (currentValue) => !currentValue
      );
    }
  };

  return (
    <section className="testsListDiv">
      <h1 className="adminHeaders">
        Tests Available in the Database
      </h1>

      {error && (
        <p
          className="fourHFormStatus error"
          role="alert"
        >
          {error}
        </p>
      )}

      {status && (
        <p
          className="fourHFormStatus success"
          role="status"
        >
          {status}
        </p>
      )}

      <div className="addTestTopRow">
        <button
          className="addTestTopBtn"
          type="button"
          onClick={openAddTestPanel}
        >
          Add Test ID
        </button>

        {typeof setTestsActiveModal ===
          "function" && (
          <button
            className="addTestTopBtn"
            type="button"
            onClick={toggleQuestionForm}
          >
            {testsActiveModal
              ? "View Tests"
              : "Add Test Questions"}
          </button>
        )}
      </div>

      {isAddTestIdOpen && (
        <div className="addTestIdPanel">
          <h2>Add Test ID</h2>

          <form
            className="addTestIdForm"
            onSubmit={handleAddTest}
          >
            <div className="addTestIdRow">
              <div className="addTestIdField addTestIdSmallField">
                <label htmlFor="new-test-id">
                  Test ID
                </label>

                <input
                  id="new-test-id"
                  type="text"
                  className="testUpdateInputs"
                  value={nextTestId}
                  readOnly
                />
              </div>

              <div className="addTestIdField addTestIdTitleField">
                <label htmlFor="new-test-title">
                  Test Title
                </label>

                <input
                  id="new-test-title"
                  type="text"
                  className="testUpdateInputs"
                  value={newTestTitle}
                  onChange={(event) =>
                    setNewTestTitle(
                      event.target.value
                    )
                  }
                  required
                />
              </div>

              <div className="addTestIdActions">
                <button
                  type="submit"
                  className="updateUserBtn"
                  disabled={isAddingTest}
                >
                  {isAddingTest
                    ? "Saving..."
                    : "Save Test"}
                </button>

                <button
                  type="button"
                  className="deleteUserBtn"
                  onClick={closeAddTestPanel}
                  disabled={isAddingTest}
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      <div className="listOfTests">
        {testsActiveModal ? (
          <>
            <h2>Questions to Add to Test</h2>
            <AddTestForm />
          </>
        ) : tests.length === 0 ? (
          <p className="fourHEmptyMessage">
            No tests were found.
          </p>
        ) : (
          <div className="fourHTableWrapper">
            <table className="fourHAdminTable">
              <thead>
                <tr>
                  <th scope="col">Test ID</th>
                  <th scope="col">Test Title</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>

              <tbody>
                {tests.map((test) => {
                  const isUpdating =
                    updatingId === test.id;

                  const isDeleting =
                    deletingId === test.id;

                  return (
                    <tr key={test.id}>
                      <td data-label="Test ID">
                        {test.id}
                      </td>

                      <td data-label="Test Title">
                        <label
                          className="sr-only"
                          htmlFor={`test-title-${test.id}`}
                        >
                          Test {test.id} title
                        </label>

                        <input
                          id={`test-title-${test.id}`}
                          type="text"
                          className="questionUpdateInputs"
                          value={
                            formData[test.id]?.title ||
                            ""
                          }
                          onChange={(event) =>
                            handleInputChange(
                              test.id,
                              event.target.value
                            )
                          }
                        />
                      </td>

                      <td data-label="Actions">
                        <div className="adminTableActions">
                          <button
                            type="button"
                            className="updateUserBtn"
                            onClick={() =>
                              handleUpdate(test.id)
                            }
                            disabled={
                              isUpdating ||
                              isDeleting
                            }
                          >
                            {isUpdating
                              ? "Updating..."
                              : "Update Test"}
                          </button>

                          <button
                            type="button"
                            className="deleteUserBtn"
                            onClick={() =>
                              handleDelete(test.id)
                            }
                            disabled={
                              isUpdating ||
                              isDeleting
                            }
                          >
                            {isDeleting
                              ? "Deleting..."
                              : "Delete Test"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}